import React from "react"
import type { IconName } from "../icon/assets/icon-map"
import { Icon } from "../icon"
import VisuallyHidden from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"
import { cn } from "../utils/cn"

// =============================================================================
// Props
// =============================================================================

export type NavigationDockTriggerProps = {
  /** Accessible name. Defaults to "Open navigation" / "Close navigation". */
  accessibleName?: string
}

// =============================================================================
// Styles
// =============================================================================

const triggerStyles = [
  "pointer-events-auto",
  "flex items-center justify-center shrink-0",
  "h-12 w-12 rounded-full",
  "bg-white ring-1 ring-inset ring-gray-200",
  "text-gray-900",
  "hover:bg-gray-100",
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900",
  "transition-colors"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Toggles the shared sidebar open/closed state.
 * Must be rendered inside `Navigation` context.
 */
export function NavigationDockTrigger({
  accessibleName
}: NavigationDockTriggerProps) {
  const { collapsed, setCollapsed } = useNavigationContext()
  const defaultAccessibleName = collapsed
    ? "Open navigation"
    : "Close navigation"
  const icon: IconName = collapsed ? "menu" : "close"

  return (
    <button
      type="button"
      data-slot="navigation-dock-trigger"
      aria-expanded={!collapsed}
      className={cn(triggerStyles)}
      onClick={() => setCollapsed(!collapsed)}
    >
      <VisuallyHidden>{accessibleName ?? defaultAccessibleName}</VisuallyHidden>
      <span className="flex flex-col items-center" aria-hidden="true">
        <Icon name={icon} />
        <span className="-mt-1 text-[0.625rem] leading-4 font-semibold">
          Menu
        </span>
      </span>
    </button>
  )
}

NavigationDockTrigger.displayName = "Navigation.Dock.Trigger"
